/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, VStack, Link } from 'chakra-v3';
import MinimalHeader from '../components/headers/MinimalHeader';
import { Button } from '../components/buttons/Index';
import { Input } from '../components/inputs/Index';
import CardShell from '../components/CardShell';
import { Text } from '../components/Text';
import { useOtpLogin, type LoginStep } from '../features/auth/hooks/useOtpLogin';
import { handlePostLoginRedirect } from '../utils/auth';
import { useAutoLogin } from '@/features/auth/hooks/useAutoLogin';

/**
 * Gets the button text based on the login step and loading state.
 *
 * @param step - The current login step
 * @param isLoading - Whether an operation is in progress
 * @returns The appropriate button text
 */
const getLoginButtonText = (step: LoginStep, isLoading: boolean): string => {
  if (step === 'LOGGED_IN') {
    return 'Logged In';
  }

  if (isLoading) {
    return 'Verifying...';
  }

  return step === 'PHONE' ? 'Generate OTP' : 'Login';
};

/**
 * Handles form submission based on the current login step.
 *
 * @param event - The form submit event
 * @param step - The current login step
 * @param handleGenerateOtp - Function to generate OTP
 * @param handleVerifyOtp - Function to verify OTP
 */
const handleLoginSubmit = (
  event: React.FormEvent,
  step: LoginStep,
  handleGenerateOtp: () => void,
  handleVerifyOtp: () => void,
): void => {
  event.preventDefault();
  if (step === 'PHONE') {
    void handleGenerateOtp();
    return;
  }
  void handleVerifyOtp();
};

/**
 * Props for the ResendOtpLink component
 */
interface ResendOtpLinkProps {
  canResendOtp: boolean;
  resendCooldown: number;
  onResend: () => void;
}

/**
 * Component for displaying resend OTP link with cooldown timer.
 *
 * Shows a clickable link when resend is available, or a countdown message during cooldown.
 */
const ResendOtpLink: React.FC<ResendOtpLinkProps> = ({ canResendOtp, resendCooldown, onResend }) => {
  if (canResendOtp) {
    return (
      <Link
        variant="underline"
        onClick={(e) => {
          e.preventDefault();
          onResend();
        }}
        cursor="pointer"
        color="text.grey20"
        _hover={{
          color: 'text.primary',
        }}
      >
        Resend OTP
      </Link>
    );
  }

  return (
    <Text variant="body" color="text.grey20">
      Resend OTP in {resendCooldown}s
    </Text>
  );
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const {
    step,
    phoneNumber,
    otpInput,
    phoneError,
    otpError,
    isLoading,
    resendCooldown,
    canResendOtp,
    setOtpInput,
    handlePhoneNumberChange,
    handleGenerateOtp,
    handleVerifyOtp,
    handleResendOtp,
  } = useOtpLogin({
    onLoginSuccess: (worker) => {
      handlePostLoginRedirect(worker, navigate);
    },
  });

  // Attempt auto-login on component mount
  useAutoLogin({
    onSuccess: (worker) => {
      handlePostLoginRedirect(worker, navigate);
    },
  });

  return (
    <Box minH="100vh" bg="bg.primary">
      <MinimalHeader />
      <Flex minH="calc(100vh - 112px)" align="center" justify="center" p="4">
        <CardShell
          w={{ base: '100%', md: '720px' }}
          contentProps={{
            bg: 'bg.primary',
            border: '1px solid',
            borderColor: 'brand.teal',
            borderRadius: '4px',
          }}
        >
          <VStack
            as="form"
            onSubmit={(event) => handleLoginSubmit(event, step, handleGenerateOtp, handleVerifyOtp)}
            gap="40px"
            py="60px"
            px={{ base: '24px', md: '90px' }}
            w="100%"
          >
            <Text as="h1" variant="heroHeading">
              Welcome to Karya Evals!
            </Text>

            <VStack w="100%" gap="32px" alignItems="stretch">
              <Input
                label="Sign in with Phone Number"
                labelColor="brand.beige"
                type="tel"
                placeholder="Enter 10-digit number"
                value={phoneNumber}
                onChange={(e) => handlePhoneNumberChange(e.target.value)}
                disabled={isLoading || step === 'LOGGED_IN'}
                isInvalid={Boolean(phoneError)}
                errorText={phoneError}
                isRequired
              />

              {step === 'OTP' && (
                <VStack w="100%" gap="8px" alignItems="stretch">
                  <Input
                    label="Enter OTP"
                    labelColor="brand.beige"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="6-digit code"
                    value={otpInput}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setOtpInput(digitsOnly);
                    }}
                    maxLength={6}
                    disabled={isLoading}
                    isInvalid={Boolean(otpError)}
                    errorText={otpError}
                    isRequired
                  />
                  <Box alignSelf="flex-end">
                    <ResendOtpLink
                      canResendOtp={canResendOtp}
                      resendCooldown={resendCooldown}
                      onResend={handleResendOtp}
                    />
                  </Box>
                </VStack>
              )}

              <Button
                type="submit"
                variant="cta"
                w={{ base: '100%', md: '150px' }}
                alignSelf={{ base: 'stretch', md: 'center' }}
                disabled={isLoading || step === 'LOGGED_IN'}
                aria-busy={isLoading}
              >
                {getLoginButtonText(step, isLoading)}
              </Button>
            </VStack>
          </VStack>
        </CardShell>
      </Flex>
    </Box>
  );
};

export default Login;
